import { createBrowserHistory } from 'history';
import { createStore, applyMiddleware, Dispatch, combineReducers } from 'redux';
import { createEpicMiddleware, combineEpics } from 'redux-observable';
import { routerMiddleware } from 'connected-react-router';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import historyReducer from './history/reducer';
import authReducer from './auth/reducer';
import authEpic from './auth/effects';
import activeProfileReducer from './active-profile/reducer';
import activeProfileEpic from './active-profile/effects';
import AppState from './state';
import { Actions } from './actions';
import historyEpic from './history/effects';
import uiReducer from './ui/reducer';
import uiEpic from './ui/effects';
import userReducer from './user/reducer';
import userEpic from './user/effects';
import setupAmplifyIntegration from './auth/amplify-integration';
import artistReducer from './artist/reducer';
import artistEpic from './artist/effects';
import { releaseReducer } from './release/reducer';
import releaseEpic from './release/effects';
import { categoriesReducer } from './categories/reducer';
import categoriesEpic from './categories/effects';
import analyticsEpic from './analytics/effects';
import { invitesReducer } from './invites/reducer';
import invitesEpic from './invites/effects';
import { recordLabelReducer } from './record-labels/reducer';
import recordLabelsEpic from './record-labels/effects';
import { productReducer } from './products/reducer';
import productsEpic from './products/effects/index';
import tracLinkReducer from './traclink/reducer';
import tracLinkEpic from './traclink/effects/index';
import { tracksReducer } from './tracks/reducer';
import tracksEpic from './tracks/effects';
import { assetsReducer } from './assets/reducer';
import assetsEpic from './assets/effects/index';
import { ownershipReducer } from './ownership/reducer';
import ownershipEpic from './ownership/effects/index';
import { collaboratorReducer } from './collaborator/reducer';
import collaboratorEpic from './collaborator/effects/index';
import { subdomainReducer } from './subdomains/reducer';
import subdomainEpic from './subdomains/effects/index';
import { subscriptionReducer } from './subscription/reducer';
import subscriptionEpic from './subscription/effects/index';
import { orderReducer } from './orders/reducer';
import ordersEpic from './orders/effects/index';
import { royaltiesReducer } from './royalties/reducer';
import royaltiesEpic from './royalties/effects/index';
import { artistAnalyticsReducer } from './artist-analytics/reducer';
import artistAnalyticsEpic from './artist-analytics/effects/index';
import { leadsReducer } from './leads/reducer';
import leadsEpic from './leads/effects/index';
import { earningsReducer } from './earnings/reducer';
import earningsEpic from './earnings/effects/index';
import { artistPresetsReducer } from './artist-presets/reducer';
import artistPresetsEpic from './artist-presets/effects';
import tracPageReducer from './tracpage/reducer';
import tracPageEpic from './tracpage/effects';
import { catalogReducer } from './catalog/reducer';
import catalogEpic from './catalog/effects';
import { notificationsReducer } from './notifications/reducer';
import notificationsEpic from './notifications/effects';
import { paymentReducer } from './payment/reducer';
import paymentEpic from './payment/effects';
import { subscriptionManagerReducer } from './subscription-manager/reducer';
import subscriptionManagerEpic from './subscription-manager/effects';
import { merchReducer } from './merch/reducer';
import merchEpic from './merch/effects';
import { termsReducer } from './terms/reducer';
import termsEpic from './terms/effects';
import { onboardingReducer } from './onboarding/reducer';
import onboardingEpic from './onboarding/effects';
import { updateTracPageSuccessEpic } from './tracpage/effects/update-tracpage-success';
import { stripeConnectReducer } from './stripe-connect/reducer';
import stripeConnectEpic from './stripe-connect/effects';
import { importCatalogReducer } from './import-catalog/reducer';
import importCatalogEpic from './import-catalog/effects';
import { rostersReducer } from './rosters/reducer';
import rostersEpic from './rosters/effects';
import { teamsReducer } from './teams/reducer';
import teamsEpic from './teams/effects';
import { dealsReducer } from './deals/reducer';
import dealsEpic from './deals/effects';

const epicMiddleware = createEpicMiddleware<Actions, Actions, AppState, any>();

const classMiddleware = () => (next: Dispatch<Actions>) => (action: Actions) =>
  next({ ...action });

export const history = createBrowserHistory({
  basename: process.env.REACT_APP_BASENAME,
});

const store = createStore(
  combineReducers({
    router: historyReducer(history),
    auth: authReducer,
    activeProfile: activeProfileReducer,
    ui: uiReducer,
    user: userReducer,
    artist: artistReducer,
    release: releaseReducer,
    assets: assetsReducer,
    tracks: tracksReducer,
    categories: categoriesReducer,
    invites: invitesReducer,
    recordLabels: recordLabelReducer,
    products: productReducer,
    traclink: tracLinkReducer,
    ownership: ownershipReducer,
    collaborator: collaboratorReducer,
    subdomain: subdomainReducer,
    subscription: subscriptionReducer,
    subscriptionManager: subscriptionManagerReducer,
    orders: orderReducer,
    royalties: royaltiesReducer,
    artistAnalytics: artistAnalyticsReducer,
    leads: leadsReducer,
    earnings: earningsReducer,
    presets: artistPresetsReducer,
    tracpage: tracPageReducer,
    catalog: catalogReducer,
    notifications: notificationsReducer,
    payment: paymentReducer,
    merch: merchReducer,
    terms: termsReducer,
    onboarding: onboardingReducer,
    stripeConnect: stripeConnectReducer,
    importCatalog: importCatalogReducer,
    rosters: rostersReducer,
    teams: teamsReducer,
    deals: dealsReducer
  }),
  composeWithDevTools(
    applyMiddleware(classMiddleware, routerMiddleware(history), epicMiddleware)
  )
);

epicMiddleware.run(
  combineEpics(
    authEpic,
    activeProfileEpic,
    historyEpic,
    uiEpic,
    userEpic,
    artistEpic,
    releaseEpic,
    assetsEpic,
    tracksEpic,
    ownershipEpic,
    collaboratorEpic,
    categoriesEpic,
    invitesEpic,
    recordLabelsEpic,
    productsEpic,
    analyticsEpic,
    tracLinkEpic,
    subdomainEpic,
    subscriptionEpic,
    subscriptionManagerEpic,
    ordersEpic,
    royaltiesEpic,
    artistAnalyticsEpic,
    leadsEpic,
    earningsEpic,
    artistPresetsEpic,
    tracPageEpic,
    catalogEpic,
    notificationsEpic,
    paymentEpic,
    merchEpic,
    termsEpic,
    onboardingEpic,
    updateTracPageSuccessEpic,
    stripeConnectEpic,
    importCatalogEpic,
    rostersEpic,
    teamsEpic,
    dealsEpic
  )
);

setupAmplifyIntegration(store.dispatch);

export default store;
